import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ToastrService } from 'ngx-toastr';
import { CustomerService } from '../../services/customer-service/customer.service';
import { RoleService } from '../../services/role-service/role.service';
import {
  CreateUserApiPayload,
  UpdateUserPayload,
  UserListRow,
  UserService,
} from '../../services/user-service/user.service';
import { RbacService } from '../../services/rbac/rbac.service';
import {
  USER_CREATE_DUPLICATE_MESSAGE,
  USER_CREATE_STATUS_FALLBACKS,
  resolveApiErrorMessage,
} from '../../services/api-error-message.util';
import { getBusinessEmailValidators } from '../../services/business-email.validators';
import {
  PERSON_NAME_INVALID_MESSAGE,
  getPersonNameValidators,
  isPersonNameDigitKey,
  sanitizePersonNameInput,
} from '../../services/person-name.validators';
import {
  getFormFieldErrorMessage,
  isFormFieldInvalid,
} from '../../services/form-field-error.util';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import {
  bindFormDirtyTracking,
  clearFormUnsavedChanges,
} from '../../services/unsaved-changes/unsaved-changes-form.util';

const USER_FORM_DIRTY_SOURCE = 'user-form-modal';

export interface AddUserDialogData {
  mode?: 'add' | 'edit';
  rowData?: UserListRow;
  customerId?: number | string;
}

export interface AddUserDialogResult {
  success: true;
}

interface CustomerAssignmentOption {
  id: number | string;
  name: string;
}

interface RoleOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-add-user-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './add-user-modal.component.html',
  styleUrl: './add-user-modal.component.scss',
})
export class AddUserModalComponent implements OnInit, OnDestroy {
  private static readonly FIELD_LABELS: Readonly<Record<string, string>> = {
    firstName: 'First Name',
    lastName: 'Last Name',
    emailId: 'Email ID',
    customerAssignment: 'Customer Assignment',
    userRoles: 'User Roles',
  };

  private readonly fb = inject(FormBuilder);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly unsavedChanges = inject(UnsavedChangesTrackerService);
  private readonly dialogRef = inject(
    MatDialogRef<AddUserModalComponent, AddUserDialogResult | undefined>
  );
  private readonly customerService = inject(CustomerService);
  private readonly roleService = inject(RoleService);
  private readonly userService = inject(UserService);
  private readonly toastr = inject(ToastrService);
  private readonly rbacService = inject(RbacService);
  readonly data = inject<AddUserDialogData>(MAT_DIALOG_DATA);

  @ViewChild('customerSearchInput') customerSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('roleSearchInput') roleSearchInput?: ElementRef<HTMLInputElement>;

  addUserForm!: FormGroup;
  isSubmitting = false;
  isLoadingCustomers = false;
  isLoadingRoles = false;
  customerOptions: CustomerAssignmentOption[] = [];
  customerSearchText = '';
  /** Full role list from API — used for chip labels in edit mode. */
  allRoleOptions: RoleOption[] = [];
  /** Roles the logged-in user is allowed to assign. */
  assignableRoleOptions: RoleOption[] = [];
  roleSearchText = '';
  readonly personNameInvalidMessage = PERSON_NAME_INVALID_MESSAGE;

  get isEditMode(): boolean {
    return this.data?.mode === 'edit';
  }

  get isAddMode(): boolean {
    return !this.isEditMode;
  }

  get modalTitle(): string {
    return this.isEditMode ? 'Edit User' : 'Add User';
  }

  get submitButtonText(): string {
    if (this.isSubmitting) {
      return this.isEditMode ? 'Updating...' : 'Adding...';
    }
    return this.isEditMode ? 'Update User' : 'Add User';
  }

  /** Submit stays disabled until every validated control is valid. */
  get isSubmitDisabled(): boolean {
    return !this.addUserForm || this.addUserForm.invalid || this.isSubmitting;
  }

  ngOnInit(): void {
    this.addUserForm = this.fb.group({
      firstName: [
        '',
        getPersonNameValidators({ required: true, minLength: 2, maxLength: 100 }),
      ],
      lastName: [
        '',
        getPersonNameValidators({ required: true, minLength: 2, maxLength: 100 }),
      ],
      emailId: ['', getBusinessEmailValidators({ required: this.isAddMode })],
      enableAdminAccess: [true],
      customerAssignment: [[], Validators.required],
      userRoles: [[], Validators.required],
      isActive: [true],
    });
    if (!this.isEditMode) {
      this.addUserForm.get('isActive')?.disable({ emitEvent: false });
    }

    bindFormDirtyTracking(
      this.addUserForm,
      USER_FORM_DIRTY_SOURCE,
      this.unsavedChanges,
      this.destroyRef
    );
    this.applyEmailFieldRules();
    this.loadCustomerAssignments();
    this.rbacService.ensureContextLoaded().subscribe(() => {
      this.loadRoles();
    });
  }

  ngOnDestroy(): void {
    this.unsavedChanges.clear(USER_FORM_DIRTY_SOURCE);
  }

  isFieldInvalid(controlName: string): boolean {
    return isFormFieldInvalid(this.addUserForm, controlName);
  }

  getFieldErrorMessage(controlName: string): string | null {
    return getFormFieldErrorMessage(
      this.addUserForm,
      controlName,
      AddUserModalComponent.FIELD_LABELS,
      { personNameInvalidMessage: this.personNameInvalidMessage }
    );
  }

  /** Prevent digit entry in person-name fields (First / Last Name). */
  onPersonNameKeydown(event: KeyboardEvent): void {
    if (isPersonNameDigitKey(event)) {
      event.preventDefault();
    }
  }

  /** Strip digits after paste / autofill into person-name fields. */
  onPersonNameInput(controlName: string, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const sanitized = sanitizePersonNameInput(input.value);
    if (sanitized === input.value) {
      return;
    }
    input.value = sanitized;
    this.addUserForm.get(controlName)?.setValue(sanitized, { emitEvent: true });
  }

  /** Focus the customer search box when the panel opens; clear search text on close. */
  onCustomerPanelOpenedChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => this.customerSearchInput?.nativeElement.focus());
    } else {
      this.customerSearchText = '';
    }
  }

  /** Focus the role search box when the panel opens; clear search text on close. */
  onRolePanelOpenedChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => this.roleSearchInput?.nativeElement.focus());
    } else {
      this.roleSearchText = '';
    }
  }

  onCancel(): void {
    if (this.isSubmitting) return;
    clearFormUnsavedChanges(
      this.addUserForm,
      USER_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );
    this.dialogRef.close();
  }

  private applyEmailFieldRules(): void {
    const ctrl = this.addUserForm?.get('emailId');
    if (!ctrl) {
      return;
    }
    ctrl.enable({ emitEvent: false });
    ctrl.setValidators(getBusinessEmailValidators({ required: this.isAddMode }));
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  onSubmit(): void {
    if (this.isSubmitting) return;
    if (this.addUserForm.invalid) {
      this.addUserForm.markAllAsTouched();
      setTimeout(() => this.focusFirstInvalidControl());
      return;
    }

    const raw = this.addUserForm.getRawValue();
    const customerIds = this.resolveCustomerAssignmentIds(raw);

    if (customerIds.length === 0) {
      this.toastr.error(
        'Customer assignment is required.',
        this.isEditMode ? 'Update Failed' : 'Create Failed',
        { timeOut: 5000, closeButton: true, progressBar: true }
      );
      return;
    }

    this.isSubmitting = true;
    const rawRoleIds: unknown[] = Array.isArray(raw.userRoles) ? raw.userRoles : [];
    const roleIds = rawRoleIds
      .map((id: unknown) => Number(id))
      .filter((id: number): id is number => Number.isFinite(id) && id > 0)
      .filter((id) => this.isAssignableRoleId(id));
    const email = String(raw.emailId ?? '').trim();
    const primaryCustomerId = customerIds[0];

    const request$ = this.isEditMode
      ? this.userService.updateUser(this.data?.rowData?.userId ?? 0, {
          firstName: String(raw.firstName ?? '').trim(),
          lastName: String(raw.lastName ?? '').trim(),
          email,
          emailId: email,
          enableAdminAccess: raw.enableAdminAccess === true,
          customerId: primaryCustomerId,
          customerAssignmentId: primaryCustomerId,
          customerAssignmentIds: customerIds,
          roleIds,
          status: raw.isActive === true ? 'Active' : 'Inactive',
        } satisfies UpdateUserPayload)
      : this.userService.createUser({
          firstName: String(raw.firstName ?? '').trim(),
          lastName: String(raw.lastName ?? '').trim(),
          email: String(raw.emailId ?? '').trim(),
          enableAdminAccess: raw.enableAdminAccess === true,
          customerAssignmentId: primaryCustomerId,
          customerAssignmentIds: customerIds,
          roleIds,
        } satisfies CreateUserApiPayload);

    request$.subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toastr.success(
          this.isEditMode
            ? 'User has been updated successfully.'
            : 'User has been added successfully.',
          this.isEditMode ? 'Success Message' : 'User Added',
          {
          timeOut: 4000,
          closeButton: true,
          progressBar: true,
          }
        );
        clearFormUnsavedChanges(
          this.addUserForm,
          USER_FORM_DIRTY_SOURCE,
          this.unsavedChanges
        );
        this.dialogRef.close({ success: true });
      },
      error: (error: any) => {
        this.isSubmitting = false;
        const msg = resolveApiErrorMessage(error, {
          fallback: this.isEditMode
            ? 'Failed to update user. Please try again.'
            : 'Failed to add user. Please try again.',
          statusFallbacks: USER_CREATE_STATUS_FALLBACKS,
          duplicateEntryMessage: USER_CREATE_DUPLICATE_MESSAGE,
          resolveRoleName: (roleId) => this.getRoleName(roleId),
        });
        this.toastr.error(
          msg,
          this.isEditMode ? 'Update Failed' : 'Create Failed',
          {
          timeOut: 6000,
          closeButton: true,
          progressBar: true,
        });
      },
    });
  }

  get filteredRoleOptions(): RoleOption[] {
    const keyword = this.roleSearchText.trim().toLowerCase();
    const source = this.assignableRoleOptions;
    if (!keyword) {
      return source;
    }
    return source.filter((r) => r.name.toLowerCase().includes(keyword));
  }

  get filteredCustomerOptions(): CustomerAssignmentOption[] {
    const keyword = this.customerSearchText.trim().toLowerCase();
    if (!keyword) {
      return this.customerOptions;
    }
    return this.customerOptions.filter((c) =>
      c.name.toLowerCase().includes(keyword)
    );
  }

  removeRole(roleId: number): void {
    const ctrl = this.addUserForm.get('userRoles');
    if (!ctrl) return;
    const selected = Array.isArray(ctrl.value) ? [...ctrl.value] : [];
    ctrl.setValue(selected.filter((id: number) => id !== roleId));
    ctrl.markAsTouched();
  }

  removeCustomer(customerId: number | string): void {
    const ctrl = this.addUserForm.get('customerAssignment');
    if (!ctrl) return;
    const selected = Array.isArray(ctrl.value) ? [...ctrl.value] : [];
    const target = Number(customerId);
    ctrl.setValue(
      selected.filter((id: number | string) => Number(id) !== target)
    );
    ctrl.markAsTouched();
  }

  getRoleName(roleId: number): string {
    return (
      this.allRoleOptions.find((r) => r.id === roleId)?.name ??
      this.assignableRoleOptions.find((r) => r.id === roleId)?.name ??
      String(roleId)
    );
  }

  getCustomerName(customerId: number | string): string {
    const id = Number(customerId);
    return (
      this.customerOptions.find((c) => Number(c.id) === id)?.name ??
      String(customerId)
    );
  }

  private loadCustomerAssignments(): void {
    this.isLoadingCustomers = true;

    this.customerService.getCustomersV1(0, 500).subscribe({
      next: (customers) => {
        this.customerOptions = customers
          .map((c): CustomerAssignmentOption | null => {
            const id = Number(c.id);
            const name = c.customerName?.trim();
            if (!Number.isFinite(id) || id <= 0 || !name) {
              return null;
            }
            return { id, name };
          })
          .filter((c): c is CustomerAssignmentOption => c !== null)
          .sort((a, b) => a.name.localeCompare(b.name));
        this.isLoadingCustomers = false;
        if (this.isEditMode) {
          this.loadUserForEdit();
        }
      },
      error: () => {
        this.customerOptions = [];
        this.isLoadingCustomers = false;
        this.toastr.warning('Failed to load customers for assignment.', 'Load Warning', {
          timeOut: 5000,
          closeButton: true,
          progressBar: true,
        });
      },
    });
  }

  private loadRoles(): void {
    this.isLoadingRoles = true;
    const payload = {
      page: 0,
      size: 500,
      sorts: [{ field: 'roleName', direction: 'asc' as const }],
      filters: [{ field: '', operator: '', value: '' }],
    };

    this.roleService.searchRoles(payload).subscribe({
      next: (page) => {
        this.allRoleOptions = (page?.content ?? [])
          .map((r) => ({
            id: r.roleId,
            name: r.roleName ?? 'N/A',
          }))
          .filter((r) => r.id != null && !!r.name);
        this.applyAssignableRoleOptions();
        this.isLoadingRoles = false;
      },
      error: () => {
        this.allRoleOptions = [];
        this.assignableRoleOptions = [];
        this.isLoadingRoles = false;
        this.toastr.warning('Failed to load user roles.', 'Load Warning', {
          timeOut: 5000,
          closeButton: true,
          progressBar: true,
        });
      },
    });
  }

  private applyAssignableRoleOptions(): void {
    this.assignableRoleOptions = this.allRoleOptions.filter((role) =>
      this.rbacService.canAssignUserRole(role.name)
    );
    this.syncSelectedRolesWithAssignableOptions();
  }

  private syncSelectedRolesWithAssignableOptions(): void {
    if (!this.allRoleOptions.length) {
      return;
    }
    const ctrl = this.addUserForm.get('userRoles');
    if (!ctrl) {
      return;
    }
    const selected = Array.isArray(ctrl.value) ? [...ctrl.value] : [];
    const allowed = selected.filter((id: number) => this.isAssignableRoleId(id));
    if (allowed.length !== selected.length) {
      ctrl.setValue(allowed);
    }
  }

  private isAssignableRoleId(roleId: number): boolean {
    const role = this.allRoleOptions.find((item) => item.id === roleId);
    if (!role) {
      return false;
    }
    return this.rbacService.canAssignUserRole(role.name);
  }

  private loadUserForEdit(): void {
    const userId = this.data?.rowData?.userId;
    const customerIds = this.resolveCustomerAssignmentIds({
      customerAssignment:
        this.data?.rowData?.customerAssignmentIds ??
        this.data?.rowData?.customerAssignmentId ??
        this.data?.rowData?.customerId,
    });
    const customerId = customerIds[0] ?? null;

    if (userId == null || customerId == null) {
      return;
    }

    this.userService.getUserById(userId, customerId).subscribe({
      next: (user) => {
        this.patchEditForm({
          firstName: this.resolveNamePart(user.username, 'first'),
          lastName: this.resolveNamePart(user.username, 'last'),
          emailId: user.email === 'N/A' ? '' : user.email,
          enableAdminAccess: user.enableAdminAccess === true,
          customerAssignmentIds:
            user.customerAssignmentIds ??
            (user.customerAssignmentId != null || user.customerId != null
              ? [user.customerAssignmentId ?? user.customerId!]
              : customerIds),
          userRoles: user.roleIds ?? [],
          isActive: String(user.status).toUpperCase() === 'ACTIVE',
        });
      },
      error: () => {
        this.patchEditForm({
          firstName: this.resolveNamePart(this.data?.rowData?.username, 'first'),
          lastName: this.resolveNamePart(this.data?.rowData?.username, 'last'),
          emailId: this.data?.rowData?.email === 'N/A' ? '' : (this.data?.rowData?.email ?? ''),
          enableAdminAccess: this.data?.rowData?.enableAdminAccess === true,
          customerAssignmentIds:
            this.data?.rowData?.customerAssignmentIds ??
            (this.data?.rowData?.customerAssignmentId != null ||
            this.data?.rowData?.customerId != null
              ? [
                  this.data?.rowData?.customerAssignmentId ??
                    this.data?.rowData?.customerId!,
                ]
              : customerIds),
          userRoles: this.data?.rowData?.roleIds ?? [],
          isActive: String(this.data?.rowData?.status).toUpperCase() === 'ACTIVE',
        });
      },
    });
  }

  private patchEditForm(values: {
    firstName: string;
    lastName: string;
    emailId: string;
    enableAdminAccess: boolean;
    customerAssignmentIds: Array<number | string>;
    userRoles: number[];
    isActive: boolean;
  }): void {
    const assignmentIds = (values.customerAssignmentIds ?? [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    this.addUserForm.patchValue({
      firstName: values.firstName,
      lastName: values.lastName,
      emailId: values.emailId,
      enableAdminAccess: values.enableAdminAccess,
      customerAssignment: assignmentIds,
      userRoles: values.userRoles,
      isActive: values.isActive,
    });
    this.addUserForm.get('isActive')?.enable({ emitEvent: false });
    this.applyEmailFieldRules();
    this.syncSelectedRolesWithAssignableOptions();
    clearFormUnsavedChanges(
      this.addUserForm,
      USER_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );
  }

  private resolveCustomerAssignmentIds(raw: {
    customerAssignment?: unknown;
  }): number[] {
    const value =
      raw.customerAssignment ??
      this.addUserForm?.get('customerAssignment')?.value ??
      this.data?.rowData?.customerAssignmentIds ??
      this.data?.rowData?.customerAssignmentId ??
      this.data?.rowData?.customerId ??
      this.data?.customerId;

    if (Array.isArray(value)) {
      return value
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }

    const id = Number(value);
    if (!Number.isFinite(id) || id <= 0) {
      return [];
    }
    return [id];
  }

  private resolveNamePart(
    fullName: string | undefined,
    mode: 'first' | 'last'
  ): string {
    const value = String(fullName ?? '').trim();
    if (!value || value === 'N/A') return '';
    const parts = value.split(/\s+/);
    if (mode === 'first') return parts[0] ?? '';
    if (parts.length <= 1) return '';
    return parts.slice(1).join(' ');
  }

  /** Scroll/focus the first invalid control in DOM order (generic — no field list). */
  private focusFirstInvalidControl(): void {
    const root = this.host.nativeElement;
    const firstInvalid = root.querySelector(
      'form input.ng-invalid, form select.ng-invalid, form textarea.ng-invalid, form .mat-mdc-select.ng-invalid'
    ) as HTMLElement | null;

    if (!firstInvalid) {
      return;
    }

    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof firstInvalid.focus === 'function') {
      firstInvalid.focus({ preventScroll: true });
    }
  }
}